import React from 'react';
import { Grid, Typography, MenuItem, Box } from '@mui/material';
import SparkCarousel from 'sharedComponents/UI/SparkCarousel';
import SourcesSkeleton from 'sharedComponents/Skeleton/SourcesSkeleton';
import { SparkEllipsisTypography, SparkImageLoader } from 'sharedComponents/UI';
import { PDFIcon, WorkSpaceIcon } from 'components/SVG';
import {
  TXTFileIcon,
  WordFileIcon,
  ExcelFileIcon,
  //   MarkDownFileIcon,
  //   TIFFFIleIcon,
  //   RawFileIcon,
  //   PNGFileIcon,
  //   JPGFileIcon,
  //   JSFiledIcon,
  //   PPTFileIcon,
  //   SVGFileIcon,
} from 'components/SVG/fileType';
import { formatDateTimeMonth } from 'utilities/time';
import { useInfiniteSparkRunRecommendationSourcesByInputParameterID } from 'hooks/SparkRuns';
import type { CommonRecommendationSchema } from 'types/Spark/SparkRunInputParameterRecommendations';

export const fileTypeToIconMap: Record<string, JSX.Element> = {
  pdf: <PDFIcon width='24px' height='24px' />,
  'text/plain': <TXTFileIcon width='24px' height='24px' />,
  'application/msword': <WordFileIcon width='24px' height='24px' />,
  xls: <ExcelFileIcon width='24px' height='24px' />,
  xlsx: <ExcelFileIcon width='24px' height='24px' />,
};

type SuggestionsPopoverProps = {
  recommendedValues: CommonRecommendationSchema;
  onMenuItemClick: (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    value: string
  ) => void;
  sparkRunInputRecommendationID: string | undefined;
};

export default function RecommendationsPopover({
  recommendedValues,
  onMenuItemClick,
  sparkRunInputRecommendationID,
}: SuggestionsPopoverProps) {
  const sourceClickHandle = (memoryID: string) => {
    window.open(`/memory/${memoryID}`);
  };

  const {
    recommendationWithSources,
    hasNextPage,
    fetchNextPage,
    isLoading: isSourcesLoading,
    isFetchingNextPage,
    totalSources,
  } = useInfiniteSparkRunRecommendationSourcesByInputParameterID(
    sparkRunInputRecommendationID
  );

  const handlePageUpdate = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Grid
      container
      direction='column'
      textAlign='left'
      spacing={2}
      marginLeft={0}
      marginTop={0}
      bgcolor='white'
      boxShadow={2}
      position='absolute'
      sx={{ zIndex: 4, maxWidth: '100%' }}
      id='spark-run-recommendation-popover'
    >
      <Grid>
        {recommendedValues?.map((recommendedValue) => (
          <MenuItem
            key={recommendedValue.value}
            dense
            divider
            onClick={(event) => onMenuItemClick(event, recommendedValue.value)}
          >
            <Box display='flex' flexDirection='column' p={1}>
              <Box display='flex' alignItems='center' gap={2}>
                <Typography fontFamily='Mont-semibold' fontSize={14}>
                  {recommendedValue.value || ''}
                </Typography>
              </Box>
              <Typography
                fontSize={14}
                sx={{
                  textWrap: 'wrap',
                  color: '#4A4A4A',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  lineHeight: 'normal',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              >
                {recommendedValue.description}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Grid>

      <Typography
        variant='body2'
        mt={2}
        ml={3}
        sx={{
          fontFamily: 'Mont-semiBold',
          fontSize: '14px',
          lineHeight: 1,
          fontWeight: 400,
        }}
      >
        {totalSources} Sources
      </Typography>
      <SparkCarousel
        onLastElementView={handlePageUpdate}
        isLoading={isSourcesLoading}
        isFetchingNextPage={isFetchingNextPage}
        childContainerStyles={{
          minWidth: '250px',
          width: '250px',
          minHeight: '120px',
          height: '120px',
          padding: '0px',
        }}
        skeletonComponent={SourcesSkeleton}
        childrenGridProps={{ gap: 3, padding: '0px 8px 8px 8px' }}
        parentContainerStyles={{ paddingRight: '16px' }}
        renderArrowButtons={false}
        skeletonCount={2}
      >
        {recommendationWithSources?.map((source) => {
          return (
            <Box
              onClick={() => sourceClickHandle(source.id)}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'space-between'}
              width={250}
              height={100}
              padding={'5px'}
              borderRadius={'12px'}
              sx={{
                backgroundColor: '#ECEFFE',
                border: '1px solid #6267AC',
                cursor: 'pointer',
              }}
            >
              <Box>
                <SparkEllipsisTypography
                  title={source.attributes.description}
                  variant='caption'
                  sx={{
                    WebkitLineClamp: 3,
                    lineHeight: 1.2,
                    paddingBottom: '2px',
                    fontSize: '13px',
                    textWrap: 'pretty',
                  }}
                >
                  {source.attributes.description}
                </SparkEllipsisTypography>
                <Typography variant='body2' sx={{ color: '#404040' }}>
                  Updated{' '}
                  {formatDateTimeMonth(
                    source.attributes.modifiedAt,
                    'D MMM YYYY'
                  )}
                </Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'}>
                {!!source.attributes.iconURL ? (
                  <SparkImageLoader
                    width={'24px'}
                    height={'24px'}
                    style={{ borderRadius: '4px' }}
                    src={source.attributes.iconURL}
                    alt={source.attributes.name}
                  />
                ) : (
                  <>
                    {source.recommendation ? (
                      <>
                        {source?.recommendation.attributes.sourceContentType ===
                        'application/msword' ? (
                          <img
                            src='/Group10555.svg'
                            alt=''
                            width='24px'
                            height='24px'
                          />
                        ) : (
                          fileTypeToIconMap[
                            source?.recommendation.attributes.sourceContentType
                          ]
                        )}
                      </>
                    ) : (
                      <WorkSpaceIcon width='24px' height='24px' />
                    )}
                  </>
                )}
                <SparkEllipsisTypography
                  title={source.attributes.description}
                  variant='caption'
                  ml={1}
                  sx={{
                    WebkitLineClamp: 1,
                    lineHeight: 1.2,
                    paddingBottom: '2px',
                    fontSize: '13px',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {source.attributes.name}
                </SparkEllipsisTypography>
              </Box>
            </Box>
          );
        })}
      </SparkCarousel>
    </Grid>
  );
}
